import React from 'react';
import { Box, Button, chakra, Dialog, Field, Input, InputGroup, Link, Portal, Stack, useDisclosure } from '@chakra-ui/react';
import { FaLock, FaUserAlt } from 'react-icons/fa';
import { signInWithEmailAndPassword, validatePassword } from 'firebase/auth';
import { useFirebaseAuth } from '../hooks/useFirebaseAuth';
import { useSendPasswordResetEmail } from 'react-firebase-hooks/auth';
import { useState } from 'react';

const CFaUserAlt = chakra(FaUserAlt);
const CFaLock = chakra(FaLock);

export const LoginModal = (props: any) => {
	const { isOpen: isModalOpen, onClose: onModalClose } = props;

	const auth = useFirebaseAuth();
	const [sendPasswordResetEmail] = useSendPasswordResetEmail(auth);

	const [email, setEmail] = useState('');
	const [emailInputErrorMessage, setEmailInputErrorMessage] = useState('');
	const handleEmailChange = (e: any) => setEmail(e.target.value);

	const [pass, setPass] = useState('');
	const [passInputErrorMessage, setPassInputErrorMessage] = useState('');
	const handlePassChange = (e: any) => setPass(e.target.value);

	const validateEmail = (email: string) => {
		const isValid = Boolean(
			String(email)
				.toLowerCase()
				.match(
					/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
				)
		);
		if (isValid) setEmailInputErrorMessage('');
		else setEmailInputErrorMessage('Wpisany email jest niepoprawny');
	};

	const validatePass = async (pass: string) => {
		const result = await validatePassword(auth, pass);
		if (result.isValid) setPassInputErrorMessage('');
		else if (!result.meetsMinPasswordLength)
			setPassInputErrorMessage(
				`Hasło musi mieć co najmniej ${result.passwordPolicy.customStrengthOptions.minPasswordLength} znaków`
			);
		else if (!result.meetsMaxPasswordLength)
			setPassInputErrorMessage(
				`Hasło musi mieć co najwyżej ${result.passwordPolicy.customStrengthOptions.maxPasswordLength} znaków`
			);
	};

	const [showPassword, setShowPassword] = useState(false);
	const handleShowClick = () => setShowPassword(!showPassword);

	const { open: isResetDialogOpen, onOpen, onClose } = useDisclosure();
	const cancelRef = React.useRef(null);

	const handleLogin = async () => {
		try {
			await signInWithEmailAndPassword(auth, email, pass);
		} catch (err: any) {
			if (err.code === 'auth/invalid-email')
				setEmailInputErrorMessage('Wpisany email jest niepoprawny');
			if (err.code === 'auth/user-not-found')
				setEmailInputErrorMessage(
					'Nie znaleziono konta z podanym adresem email'
				);
			if (err.code === 'auth/missing-password')
				setPassInputErrorMessage('Hasło nie może być puste');
			if (err.code === 'auth/wrong-password')
				setPassInputErrorMessage('Hasło nie jest poprawne');
		}
	};

	return (
        <Dialog.Root
            open={isModalOpen}
            onOpenChange={(details) => {
                if (!details.open) {
                    onModalClose();
                }
            }}
        >
            <Dialog.Backdrop />
            <Dialog.Positioner>
                <Dialog.Content>
                    <Dialog.Header>Zaloguj się</Dialog.Header>
                    <Dialog.CloseTrigger />
                    <Dialog.Body>
                        <Box>
                            <form>
                                <Stack gap={4} p="1rem">
                                    <Field.Root
                                        invalid={Boolean(emailInputErrorMessage)}
                                    >
                                        <InputGroup startElement={<CFaUserAlt pointerEvents="none" color="gray.300" />}>
                                            <Input
                                                value={email}
                                                onChange={handleEmailChange}
                                                onBlur={() => validateEmail(email)}
                                                type="email"
                                                placeholder="Email"
                                            />
                                        </InputGroup>
                                        <Field.ErrorText>
                                            {emailInputErrorMessage}
                                        </Field.ErrorText>
                                    </Field.Root>
                                    <Field.Root
                                        invalid={Boolean(passInputErrorMessage)}
                                    >
                                        <InputGroup startElement={<CFaLock pointerEvents="none" color="gray.300" />} endElement={<Button
                                            width="4.5rem"
                                            h="1.75rem"
                                            size="sm"
                                            onClick={handleShowClick}>{showPassword ? 'Ukryj' : 'Pokaż'}</Button>}
                                            >
                                            <Input
                                                value={pass}
                                                onChange={handlePassChange}
                                                onBlur={() => validatePass(pass)}
                                                type={
                                                    showPassword
                                                        ? 'text'
                                                        : 'password'
                                                }
                                                placeholder="Hasło"
                                            />
                                        </InputGroup>
                                        <Field.ErrorText>
                                            {passInputErrorMessage}
                                        </Field.ErrorText>
                                        <Field.HelperText textAlign="right">
                                            {email && !emailInputErrorMessage ? (
                                                <Link onClick={onOpen}>
                                                    zapomniałeś hasła?
                                                </Link>
                                            ) : (
                                                <>zapomniałeś hasła?</>
                                            )}
                                        </Field.HelperText>
                                    </Field.Root>
                                </Stack>
                            </form>
                        </Box>
                        <Dialog.Root
                            open={isResetDialogOpen}
                            initialFocusEl={() => cancelRef.current}
                            role='alertdialog'
                            onOpenChange={e => {
                                if (!e.open) {
                                    onClose();
                                }
                            }}>
                            <Portal>
                                <Dialog.Backdrop>
                                    <Dialog.Positioner>
                                        <Dialog.Content>
                                            <Dialog.Header
                                                fontSize="lg"
                                                fontWeight="bold"
                                            >
                                                Reset hasła
                                            </Dialog.Header>
                                            <Dialog.Body>
                                                Na email <strong>{email}</strong> zostanie
                                                wysłana wiadomość powalająca na zresetowanie
                                                istniejącego hasła.
                                            </Dialog.Body>
                                            <Dialog.Footer>
                                                <Button ref={cancelRef} onClick={onClose}>
                                                    Anuluj
                                                </Button>
                                                <Button
                                                    colorScheme="red"
                                                    onClick={() => {
                                                        sendPasswordResetEmail(email);
                                                        onClose();
                                                        setPass('');
                                                    }}
                                                    ml={3}
                                                >
                                                    Resetuj
                                                </Button>
                                            </Dialog.Footer>
                                        </Dialog.Content>
                                    </Dialog.Positioner>
                                </Dialog.Backdrop>
                            </Portal>
                        </Dialog.Root>
                    </Dialog.Body>

                    <Dialog.Footer>
                        <Button variant="ghost" mr={3} onClick={onModalClose}>
                            Anuluj
                        </Button>
                        <Button
                            disabled={
                                !email ||
                                !pass ||
                                Boolean(emailInputErrorMessage) ||
                                Boolean(passInputErrorMessage)
                            }
                            colorScheme="orange"
                            onClick={handleLogin}
                        >
                            Zaloguj
                        </Button>
                    </Dialog.Footer>
                </Dialog.Content>
            </Dialog.Positioner>
        </Dialog.Root>
    );
};
